import pluralize from "pluralize"
import {
  DioboxEvent, Form, FormType, ModalName,
} from "sharedTypes"
import { hasValidCustomDomain } from "utilities/strings"
import {
  eventBadgesPath,
  eventFormsPath,
  eventPortalPath,
  eventSeatingPath,
  eventSettingsPath,
  eventSubmissionsPath,
  eventTextingPath,
  eventWalletPassSettingsPath,
  eventWebPagePath,
  mobileAppURL,
} from "utilities/routes"
import { EventUserRole } from "services/permissions"
import { ChecklistStep } from "./cards/ChecklistCard"
import { EventPhase } from "./phase"

const SIGN_UP_FORMS = [FormType.Registration, FormType.RSVP, FormType.Ticket]

const DOOR_ROLES = [EventUserRole.DoorOperator, EventUserRole.DoorStaff]

type Params = {
  event: DioboxEvent
  phase: EventPhase
  forms?: Form[]
  formsFailed?: boolean
  vanityUrlsEnabled: boolean
  texting: boolean
  walletPassEnabled: boolean
  badgesEnabled: boolean
  shareLink: () => void
  uploadImage: () => void
  showModal: (name: ModalName) => void
}

type Steps = { steps: ChecklistStep[], optionalSteps: ChecklistStep[], loading: boolean }

const dayOfSteps = ({ event, badgesEnabled, showModal }: Params): ChecklistStep[] => [
  ...badgesEnabled && event.badgePrintingEnabled ? [{
    id: "printBadges",
    label: "Print badge runs",
    done: false,
    to: eventBadgesPath(event.id),
  }] : [],
  {
    id: "checkInStations",
    label: "Staff check-in stations",
    done: (event.users ?? []).some(({ role }) => DOOR_ROLES.includes(role)),
    to: eventSettingsPath(event.id, "team"),
  },
  {
    id: "openCheckIn",
    label: "Open check-in",
    done: event.counts.arrivedGuests > 0,
    onClick: () => window.open(mobileAppURL(), "_blank", "noopener"),
  },
  {
    id: "arrivalReminder",
    label: "Send arrival reminder",
    done: false,
    onClick: () => showModal(ModalName.NewEmail),
  },
]

const postEventSteps = ({ event, showModal }: Params): ChecklistStep[] => {
  const { counts } = event
  const surveys = counts.formSubmissionLineItems.survey ?? 0

  return [
    {
      id: "attendanceReport",
      label: "Export attendance report",
      done: false,
      onClick: () => showModal(ModalName.ExportGuests),
    },
    {
      id: "thankYouEmail",
      label: "Send thank-you email",
      done: false,
      onClick: () => showModal(ModalName.NewEmail),
    },
    {
      id: "postEventSurvey",
      label: "Collect post-event survey",
      done: false,
      ...surveys > 0 && { hint: pluralize("survey response", surveys, true) },
      to: eventFormsPath(event.id),
    },
    ...Number(counts.grossSales) > 0 ? [{
      id: "refundNoShows",
      label: "Refund no-show deposits",
      done: false,
      to: `${eventSubmissionsPath(event.id)}?formType=${FormType.Ticket}`,
    }] : [],
  ]
}

// Before the event the checklist sets the event up; on the day and after it, it lists that
// phase's steps instead, as in the mockup.
const checklistSteps = (params: Params): Steps => {
  if (params.phase === "day-of") {
    return { steps: dayOfSteps(params), optionalSteps: [], loading: false }
  }
  if (params.phase === "post-event") {
    return { steps: postEventSteps(params), optionalSteps: [], loading: false }
  }

  return setupSteps(params)
}

const setupSteps = ({
  event, forms, formsFailed = false, vanityUrlsEnabled, texting, walletPassEnabled, badgesEnabled,
  shareLink, uploadImage, showModal,
}: Params): Steps => {
  const { id: eventId, counts, website } = event
  const { registration, rsvp, ticket } = counts.formSubmissionLineItems
  const signedUp = Boolean(registration || rsvp || ticket)
  const signUpForm = forms
    ? signedUp || forms.some((form) => SIGN_UP_FORMS.includes(form.type) && !form.disabled)
    : counts.forms > 0

  return {
    loading: !forms && !formsFailed,
    steps: [
      {
        id: "eventImage",
        label: "Upload an event image",
        done: Boolean(website.coverImageUrl),
        onClick: uploadImage,
      },
      {
        id: "eventLink",
        label: "Set up the event link",
        done: vanityUrlsEnabled ? event.slugClaimed : Boolean(website.urlPath),
        onClick: shareLink,
      },
      {
        id: "portalBranding",
        label: "Customize portal branding",
        done: Boolean(event.portalLogoUrl || event.portalEventImageUrl),
        to: eventPortalPath(eventId),
      },
      {
        id: "registrationForm",
        label: "Publish a registration form",
        done: signUpForm,
        to: eventFormsPath(eventId),
      },
      {
        id: "eventPage",
        label: "Publish the event page",
        done: website.published,
        to: eventWebPagePath(eventId),
      },
      {
        id: "importGuests",
        label: "Import guests",
        done: counts.guests > 0,
        onClick: () => showModal(ModalName.ImportGuests),
      },
      {
        id: "firstInvite",
        label: "Send the first invite or save-the-date",
        done: counts.emails > 0,
        onClick: () => showModal(ModalName.NewEmail),
      },
      {
        id: "mobileApp",
        label: "Log in to the mobile app for check-in",
        done: false,
        onClick: () => window.open(mobileAppURL(), "_blank", "noopener"),
      },
    ],
    optionalSteps: [
      {
        id: "paymentGateway",
        label: "Connect a payment gateway",
        done: Boolean(event.stripeAccountId),
        to: eventSettingsPath(eventId, "ticketing-payments"),
      },
      ...event.whiteLabel ? [{
        id: "customDomain",
        label: "Set up a custom domain",
        done: Boolean(hasValidCustomDomain(event)),
        to: eventSettingsPath(eventId, "custom-email-domains"),
      }] : [],
      ...walletPassEnabled && event.passBuilderEnabled ? [{
        id: "walletPass",
        label: "Customize the wallet pass",
        done: Object.keys(event.walletPassData ?? {}).length > 0,
        to: eventWalletPassSettingsPath(eventId),
      }] : [],
      ...badgesEnabled && event.badgePrintingEnabled ? [{
        id: "badge",
        label: "Design a badge",
        done: (counts.badges ?? 0) > 0,
        to: eventBadgesPath(eventId),
      }] : [],
      {
        id: "seatingPlan",
        label: "Build a seating plan",
        done: counts.tables > 0,
        to: eventSeatingPath(eventId),
      },
      ...texting ? [{
        id: "textMessage",
        label: "Send a text message",
        done: counts.texts > 0,
        to: eventTextingPath(eventId),
      }] : [],
    ],
  }
}

export default checklistSteps
